import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio, TextField, Button, Paper, Avatar, Tooltip, Grid, Chip, Divider, IconButton, Alert
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { KeyboardArrowRight, CheckCircle, Cancel, RestartAlt, Undo, ErrorOutline } from '@mui/icons-material';
import { API_BASE_URL } from '../services/api';
import {
  quickApproveInvoice,
  resolveInvoiceComment,
  unresolveInvoiceComment,
  resubmitInvoice,
  type CommentsMeta,
} from '../services/invoicesService';
import { useNotification } from '../components/layouts/Layout';

// ─── Helpery do parsowania komentarzy z pola `comment` ──────────────────────
// Linie mają format: `[uid] | name | YYYY-MM-DD HH:MM:SS: tekst`.
// commentId musi być spójny z backendem (sha1 całej linii, 16 hex chars).

async function sha1Hex(input: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-1', enc.encode(input));
  const arr = Array.from(new Uint8Array(buf));
  return arr.map((b) => b.toString(16).padStart(2, '0')).join('');
}

interface ParsedComment {
  commentId: string;
  raw: string;
  userId: string;
  author: string;
  dateStr: string;
  text: string;
}

async function parseComments(commentText: string | null | undefined): Promise<ParsedComment[]> {
  if (!commentText) return [];
  const lines = String(commentText).split('\n').filter(Boolean);
  const out: ParsedComment[] = [];
  for (const raw of lines) {
    const id = (await sha1Hex(raw)).slice(0, 16);
    const m = raw.match(/^\[(.*?)\]\s*\|\s*(.*?)\s*\|\s*([0-9\-:\s]{19}):\s*(.*)$/);
    if (m) {
      out.push({ commentId: id, raw, userId: m[1] || '', author: (m[2] || '').trim(), dateStr: (m[3] || '').trim(), text: (m[4] || '').trim() });
    } else {
      out.push({ commentId: id, raw, userId: '', author: '', dateStr: '', text: raw });
    }
  }
  return out;
}

const InvoiceApprovePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [decision, setDecision] = React.useState<'approved'|'rejected'|'needs_info'>('approved');
  const [comment, setComment] = React.useState('');
  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [meta, setMeta] = React.useState<any>(null);
  const [parsedComments, setParsedComments] = React.useState<ParsedComment[]>([]);
  const [resolvingId, setResolvingId] = React.useState<string | null>(null);
  const [resubmitting, setResubmitting] = React.useState(false);
  const [refreshTick, setRefreshTick] = React.useState(0);
  // `user` z useAuth() może mieć niestabilną referencj\u0119 (nowy obiekt co render
  // kontekstu), wi\u0119c trzymamy go w refie i NIE wpisujemy do deps efektu —
  // inaczej fetch listy faktur leci w nieskończoność i zamraża aplikacj\u0119.
  const userRef = React.useRef(user);
  React.useEffect(() => { userRef.current = user; }, [user]);

  const previewUrl = React.useMemo(() => {
    if (!fileUrl) return null;
    if (String(fileUrl).includes('/ksef/invoices/')) return fileUrl;
    try {
      const m1 = String(fileUrl).match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      const m2 = String(fileUrl).match(/id=([a-zA-Z0-9_-]+)/);
      const m3 = String(fileUrl).match(/\/d\/([a-zA-Z0-9_-]+)/);
      const fileId = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]) || null;
      if (fileId) return `${API_BASE_URL}/kzms/attachments/${encodeURIComponent(fileId)}/content`;
    } catch {}
    return fileUrl;
  }, [fileUrl]);

  const [saving, setSaving] = React.useState(false);
  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await quickApproveInvoice(id, decision, comment || undefined);
      showNotification('Decyzja zapisana', 'success', '/accounting/purchases');
      navigate('/accounting/purchases');
    } catch (e: any) {
      showNotification('Błąd zapisu decyzji', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Wczytaj fakturę. Reagujemy zarówno na zmianę :id, jak i ręczne odświeżenie
  // (`refreshTick`) — po resolve / resubmit chcemy mieć aktualne dane.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      try {
        const { data } = await api.get(`/transfer-invoices`);
        const row = (data?.data || []).find((x: any) => String(x.id) === String(id));
        if (cancelled) return;
        if (row?.id) {
          if (row.ksef_invoice_id) {
            const base = (api as any)?.defaults?.baseURL || '';
            const normalized = String(base).replace(/\/$/, '');
            setFileUrl(`${normalized}/ksef/invoices/${encodeURIComponent(row.ksef_invoice_id)}/pdf`);
          } else {
            const drivePreview = row.google_drive_file_url
              || (row.google_drive_file_id ? `https://drive.google.com/file/d/${row.google_drive_file_id}/preview` : null);
            if (drivePreview) {
              setFileUrl(drivePreview);
            } else {
              const base = (api as any)?.defaults?.baseURL || '';
              const normalized = String(base).replace(/\/$/, '');
              setFileUrl(`${normalized}/transfer-invoices/${row.id}/pdf`);
            }
          }
        }
        setMeta(row || null);
        // Parsujemy wszystkie linie komentarzy (wraz z deterministycznym
        // commentId), żeby później korelować je z `comments_meta`.
        const parsed = await parseComments(row?.comment || '');
        if (cancelled) return;
        setParsedComments(parsed);

        // Domyślna decyzja: jeśli rola użytkownika ma już zapisaną decyzję
        // dla tej faktury, przedstawiamy ją (ale NIE blokujemy zmiany).
        const u = userRef.current;
        const role = String(u?.role || '').toLowerCase();
        const perms = (u?.permissions || {}) as Record<string, boolean>;
        const isAccounting = role === 'księgowość' || role === 'ksiegowosc' || role === 'accounting'
          || Boolean((perms as any)?.accounting) || Boolean((perms as any)?.['księgowość']) || Boolean((perms as any)?.ksiegowosc);
        const isAdmin = role === 'admin';
        let current: 'approved'|'rejected'|'needs_info' = 'approved';
        if (isAccounting) {
          const v = String(row?.accounting_approval_status || 'approved');
          current = (v === 'rejected' ? 'rejected' : v === 'needs_info' ? 'needs_info' : 'approved');
        } else if (isAdmin) {
          const v = String(row?.admin_approval_status || 'approved');
          current = (v === 'rejected' ? 'rejected' : v === 'needs_info' ? 'needs_info' : 'approved');
        } else {
          const s = String(row?.status || '');
          current = (s === 'Odrzucone' ? 'rejected' : s === 'Uzupełnić' ? 'needs_info' : 'approved');
        }
        setDecision(current);
      } catch {
        if (!cancelled) setFileUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id, refreshTick]);

  // Memoizujemy comments_meta — surowy obiekt z API jest tworzony co render,
  // a `useMemo` poniżej wymaga stabilnej referencji żeby nie liczyć od nowa.
  const commentsMeta: CommentsMeta = React.useMemo(() => {
    if (!meta) return {};
    if (meta.comments_meta && typeof meta.comments_meta === 'object') return meta.comments_meta as CommentsMeta;
    try { return JSON.parse(String(meta.comments_meta || '{}')) || {}; } catch { return {}; }
  }, [meta]);

  // Lista powodów (rejected/needs_info) — tylko te, które mają wpis w meta
  // jako 'reason'. Linie sprzed wprowadzenia tej funkcji nie będą tutaj widoczne;
  // pojawią się dopiero po nowej decyzji księgowości/admina.
  const reasonComments = React.useMemo(() => {
    return parsedComments
      .map((c) => ({ comment: c, meta: commentsMeta[c.commentId] }))
      .filter((x) => x.meta && x.meta.type === 'reason');
  }, [parsedComments, commentsMeta]);

  const openReasons = reasonComments.filter((x) => !x.meta?.resolved);

  const status: string = String(meta?.status || '');
  const needsResubmit = status === 'Odrzucone' || status === 'Uzupełnić';
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = role === 'admin';

  const handleResolve = async (commentId: string) => {
    if (!id) return;
    setResolvingId(commentId);
    try {
      await resolveInvoiceComment(id, commentId);
      setRefreshTick((t) => t + 1);
      showNotification('Powód oznaczony jako rozwiązany', 'success');
    } catch {
      showNotification('Błąd oznaczania powodu', 'error');
    } finally {
      setResolvingId(null);
    }
  };

  const handleUnresolve = async (commentId: string) => {
    if (!id) return;
    setResolvingId(commentId);
    try {
      await unresolveInvoiceComment(id, commentId);
      setRefreshTick((t) => t + 1);
      showNotification('Cofnięto rozwiązanie powodu', 'info');
    } catch {
      showNotification('Błąd cofania', 'error');
    } finally {
      setResolvingId(null);
    }
  };

  const handleResubmit = async (force = false) => {
    if (!id) return;
    setResubmitting(true);
    try {
      await resubmitInvoice(id, { comment: comment || undefined, force });
      showNotification('Zgłoszono do ponownej weryfikacji', 'success', '/accounting/purchases');
      navigate('/accounting/purchases');
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 400 && data?.canForce) {
        showNotification('Niektóre powody są nadal otwarte. Admin może wymusić.', 'warning');
      } else {
        showNotification('Błąd zgłoszenia do ponownej weryfikacji', 'error');
      }
    } finally {
      setResubmitting(false);
    }
  };

  const resubmitDisabled = openReasons.length > 0 && !isAdmin;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, bgcolor: 'background.paper' }}>
        <Tooltip title={user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'Użytkownik'}>
          <Avatar src={user?.avatar_url || undefined} sx={{ width: 28, height: 28 }}>
            {(user?.first_name || user?.last_name) ? `${(user.first_name||'').slice(0,1)}${(user.last_name||'').slice(0,1)}`.toUpperCase() : 'U'}
          </Avatar>
        </Tooltip>
        <KeyboardArrowRight sx={{ color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>Zatwierdzenie faktury #{id}</Typography>
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2 }}>
        {/* Lewa kolumna – podgląd PDF (70%) */}
        <Box sx={{ width: { xs: '100%', md: '70%' } }}>
          <Box
            sx={{
              border: '2px dashed #9e9e9e',
              borderRadius: 1,
              height: { xs: '70vh', md: 'calc(100vh - 180px)' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: fileUrl ? 'background.paper' : 'transparent',
            }}
          >
          {previewUrl ? (
            <Box sx={{ width: '100%', height: '100%' }}>
              <iframe title="invoice-pdf" src={previewUrl} style={{ width: '100%', height: '100%', border: 0 }} />
            </Box>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
              Brak podglądu PDF
            </Box>
          )}
          </Box>
        </Box>
        {/* Prawa kolumna – szczegóły, powody i decyzja (30%) */}
        <Paper sx={{ p: 2, width: { xs: '100%', md: '30%' }, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: { md: 'calc(100vh - 180px)' }, overflowY: 'auto' }}>
          {meta && (
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Projekt</Typography>
                  <Typography variant="body2">{meta.project_id}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Kontrahent</Typography>
                  <Typography variant="body2">{meta.contractor_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Typ</Typography>
                  <Typography variant="body2">{meta.document_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Termin płatności</Typography>
                  <Typography variant="body2">{meta.due_date}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Wartość brutto</Typography>
                  <Typography variant="body2">{meta.gross_amount} PLN</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={meta.priority} />
                    <Chip
                      size="small"
                      color={meta.status === 'Zatwierdzone' ? 'success' : meta.status === 'Odrzucone' ? 'error' : meta.status === 'Uzupełnić' ? 'warning' : 'default'}
                      label={meta.status}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Sekcja powodów odrzucenia / uzupełnienia */}
          {reasonComments.length > 0 && (
            <Box>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                Powody do rozwiązania
                <Chip
                  size="small"
                  color={openReasons.length > 0 ? 'warning' : 'success'}
                  label={openReasons.length > 0 ? `${openReasons.length} otwart${openReasons.length === 1 ? 'y' : 'e'}` : 'wszystkie rozwiązane'}
                />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reasonComments.map(({ comment: c, meta: m }) => {
                  const isResolved = !!m?.resolved;
                  const kind = m?.reason_kind === 'needs_info' ? 'Uzupełnić' : 'Odrzucone';
                  const kindColor = m?.reason_kind === 'needs_info' ? '#FFA000' : '#D32F2F';
                  return (
                    <Box
                      key={c.commentId}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        border: `1px solid ${isResolved ? '#A5D6A7' : kindColor}`,
                        backgroundColor: isResolved ? '#E8F5E9' : '#FFF8E1',
                        opacity: isResolved ? 0.85 : 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={kind} sx={{ backgroundColor: kindColor, color: '#fff', fontWeight: 600, height: 20 }} />
                            <Typography variant="caption" color="text.secondary">{c.dateStr || m?.created_at || ''}</Typography>
                            {c.author && (<Typography variant="caption" color="text.secondary">• {c.author}</Typography>)}
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</Typography>
                          {isResolved && (
                            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px dashed #A5D6A7' }}>
                              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircle sx={{ fontSize: 14 }} /> Rozwiązano
                                {m?.resolved_at ? ` • ${String(m.resolved_at).slice(0,16)}` : ''}
                                {m?.resolved_by_name ? ` • ${m.resolved_by_name}` : ''}
                              </Typography>
                              {m?.resolution_text && (
                                <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>{m.resolution_text}</Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                          {isResolved ? (
                            <Tooltip title="Cofnij rozwiązanie">
                              <span>
                                <IconButton size="small" onClick={() => handleUnresolve(c.commentId)} disabled={resolvingId === c.commentId}>
                                  <Undo fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Oznacz jako rozwiązany">
                              <span>
                                <IconButton size="small" color="success" onClick={() => handleResolve(c.commentId)} disabled={resolvingId === c.commentId}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* CTA: Zgłoś do ponownej weryfikacji */}
              {needsResubmit && (
                <Box sx={{ mt: 1.5 }}>
                  {openReasons.length > 0 && !isAdmin && (
                    <Alert severity="info" icon={<ErrorOutline fontSize="small" />} sx={{ mb: 1 }}>
                      Oznacz wszystkie powody jako rozwiązane, żeby zgłosić fakturę do ponownej weryfikacji.
                    </Alert>
                  )}
                  {openReasons.length > 0 && isAdmin && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {openReasons.length} powod{openReasons.length === 1 ? '' : (openReasons.length < 5 ? 'y' : 'ów')} nadal otwart{openReasons.length === 1 ? 'y' : 'e'}. Jako admin możesz wymusić zgłoszenie.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<RestartAlt />}
                      onClick={() => handleResubmit(false)}
                      disabled={resubmitting || resubmitDisabled}
                    >
                      {resubmitting ? 'Wysyłanie…' : 'Zgłoś do ponownej weryfikacji'}
                    </Button>
                    {isAdmin && openReasons.length > 0 && (
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => handleResubmit(true)}
                        disabled={resubmitting}
                      >
                        Wymuś zgłoszenie (admin)
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Sekcja decyzji – zawsze odblokowana, niezależnie od bieżącego statusu */}
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Decyzja</Typography>
            <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value as any)}>
              <FormControlLabel value="approved" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /> Akceptuję</Box>} />
              <FormControlLabel value="rejected" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Cancel sx={{ fontSize: 18, color: 'error.main' }} /> Odrzucam</Box>} />
              <FormControlLabel value="needs_info" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ErrorOutline sx={{ fontSize: 18, color: '#FFA000' }} /> Uzupełnić</Box>} />
            </RadioGroup>
            <TextField
              label={decision === 'approved' ? 'Komentarz (opcjonalny)' : 'Powód / uzupełnienie'}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button variant="outlined" onClick={() => window.history.back()}>Anuluj</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Zapisywanie…' : 'Zapisz decyzję'}</Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default InvoiceApprovePage;
